import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  BarChart3,
  BrainCircuit,
  Blocks,
  Building2,
  Bug,
  CalendarClock,
  ChevronDown,
  FolderClosed,
  Globe,
  LayoutDashboard,
  LineChart,
  ListChecks,
  LogOut,
  Network,
  Play,
  Plus,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  ScrollText,
  Sparkles,
  Tags,
  Users,
  Wrench,
  X,
  CreditCard,
  BookOpen,
  PackagePlus,
  TicketPercent,
  Receipt,
} from 'lucide-react';
import { useAuthStore } from '@/store/auth';
import { initials } from '@/lib/format';
import { cn } from '@/lib/cn';
import { Logo } from '@/components/brand/Logo';

/**
 * Menu utama.
 *
 * Scraping dikelompokkan sebagai satu grup yang bisa dibuka: tiga layarnya
 * saling berkaitan erat dan selalu dipakai berurutan, jadi menampilkannya
 * sebagai tiga menu sejajar di tingkat atas membuat sidebar terasa acak.
 */
const NAV = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  {
    label: 'Scraping',
    icon: Blocks,
    // Grup terbuka sendiri saat pengguna sedang berada di salah satu anaknya.
    match: /^\/jobs/,
    children: [
      { to: '/jobs/new', label: 'Buat Scraping', icon: Plus },
      { to: '/jobs/running', label: 'Scraping Saat Ini', icon: Play },
      { to: '/jobs', label: 'List Job', icon: ListChecks, end: true },
    ],
  },
  { to: '/storage', label: 'Penyimpanan Pribadi', icon: FolderClosed },
  { to: '/scheduler', label: 'Scheduler', icon: CalendarClock },
  { to: '/datasets', label: 'Data Publik', icon: Globe },
  {
    label: 'Analisa',
    icon: BarChart3,
    match: /^\/analisa/,
    children: [
      { to: '/analisa/eda', label: 'EDA', icon: BarChart3 },
      { to: '/analisa/analytics', label: 'Analytics', icon: LineChart },
    ],
  },
  // Halaman ini sudah lama ada rutenya tetapi tidak pernah punya tautan, jadi
  // satu-satunya cara menemukannya adalah mengetik alamatnya. Pengguna yang
  // kuotanya habis justru orang yang paling butuh menemukannya.
  { to: '/billing', label: 'Paket & Tagihan', icon: CreditCard },
];

/**
 * Menu yang belum dibangun.
 *
 * Ditampilkan sengaja, bukan disembunyikan: pengguna perlu tahu ke mana
 * produk ini menuju, dan menandainya "Segera" lebih jujur daripada menu yang
 * bisa diklik lalu berujung halaman kosong.
 */
const SOON = [
  { label: 'Machine Learning', icon: BrainCircuit },
  { label: 'Tools', icon: Sparkles },
];

/**
 * Wilayah manajemen web: hanya tampil untuk platformRole admin dan root.
 * Isinya bukan data organisasi melainkan situsnya sendiri - konten landing,
 * harga paket, dan daftar seluruh pengguna.
 */
const MANAJEMEN = [
  { to: '/manajemen/pengguna', label: 'Pengguna', icon: Users },
  { to: '/manajemen/pelanggan', label: 'Data Pelanggan', icon: Building2 },
  { to: '/manajemen/harga', label: 'Harga Paket', icon: Tags },
  { to: '/manajemen/addon', label: 'Add-on', icon: PackagePlus },
  { to: '/manajemen/diskon', label: 'Kode Diskon', icon: TicketPercent },
  { to: '/manajemen/transaksi', label: 'Transaksi', icon: Receipt },
  { to: '/manajemen/laporan', label: 'Laporan', icon: LineChart },
  { to: '/manajemen/audit', label: 'Jejak Audit', icon: ShieldCheck },
  { to: '/manajemen/konten', label: 'Konten Landing', icon: ScrollText },
  { to: '/manajemen/proxy', label: 'Proxy', icon: Network },
  { to: '/manajemen/pengaturan', label: 'Pengaturan Situs', icon: SlidersHorizontal },
];

/** Wilayah dev: template skrip untuk scraping, EDA, analytics, ML, tools. */
const DEV = [{ to: '/dev/templates', label: 'Template Skrip', icon: Wrench }];

/**
 * Kelas menu, dipisah menurut kepentingannya.
 *
 * ── Kenapa tidak satu kelas untuk semua ──────────────────────────────────
 *
 * Dulu setiap baris memakai kelas yang sama persis. Untuk akun root itu
 * berarti sekitar 25 baris identik: "Dashboard" yang dibuka tiap hari
 * terlihat sama persis dengan "Jejak Audit" yang dibuka sebulan sekali.
 *
 * Kalau semua diberi berat yang sama, mata tidak diberi tahu harus melihat ke
 * mana, dan hasilnya terbaca kaku. Perbedaan kerapatan dan tekstur di bawah
 * ini yang menggantikannya - bukan warna baru, bukan ukuran yang dibesarkan.
 */
const itemUtama = ({ isActive }) =>
  cn(
    'group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm',
    'transition-colors duration-150',
    isActive
      ? // Pita di tepi kiri jadi jangkar "Anda di sini". Latar 8 persen saja
        // nyaris tidak terbaca di atas navy, jadi mata harus mencari tiap kali.
        [
          'bg-white/[0.11] font-semibold text-[var(--sidebar-text-active)]',
          'before:absolute before:-left-3 before:top-1/2 before:h-5 before:w-[3px]',
          'before:-translate-y-1/2 before:rounded-r-full before:bg-accent-400',
        ]
      : 'font-medium hover:bg-white/[0.07] hover:text-white',
  );

/**
 * Menu sekunder: teks saja, lebih rapat, lebih redup.
 *
 * Sebelas menu Manajemen Web tidak butuh ikon. Dua puluh lima ikon dalam satu
 * kolom selebar 256 piksel berhenti menjadi penanda dan berubah jadi tekstur.
 * Melepasnya di sini justru yang membuat menu utama menonjol, tanpa perlu
 * membesarkan apa pun.
 */
const itemSekunder = ({ isActive }) =>
  cn(
    'relative flex items-center gap-2 rounded-md py-[5px] pr-3 pl-6 text-[13px]',
    'transition-colors duration-150',
    isActive
      ? [
          'bg-white/[0.09] font-semibold text-[var(--sidebar-text-active)]',
          'before:absolute before:-left-3 before:top-1/2 before:h-4 before:w-[3px]',
          'before:-translate-y-1/2 before:rounded-r-full before:bg-accent-400',
        ]
      : 'text-white/45 hover:bg-white/[0.06] hover:text-white',
  );

/** Anak menu grup: menjorok, tanpa ikon, sedikit lebih kecil. */
const itemAnak = ({ isActive }) =>
  cn(
    'relative flex items-center rounded-md py-[5px] pr-3 pl-6 text-[13px]',
    'transition-colors duration-150',
    isActive
      ? [
          'bg-white/[0.09] font-semibold text-[var(--sidebar-text-active)]',
          'before:absolute before:-left-3 before:top-1/2 before:h-4 before:w-[3px]',
          'before:-translate-y-1/2 before:rounded-r-full before:bg-accent-400',
        ]
      : 'text-white/50 hover:bg-white/[0.06] hover:text-white',
  );

export const Sidebar = ({ open, onClose }) => {
  const { user, organization, logout } = useAuthStore();
  const { pathname } = useLocation();

  // Navbar per peran platform: user melihat menu dasar saja; admin mendapat
  // Manajemen Web; dev mendapat Template Skrip; root mendapat keduanya.
  const platformRole = user?.platformRole ?? 'user';
  const lihatManajemen = platformRole === 'admin' || platformRole === 'root';
  const lihatDev = platformRole === 'dev' || platformRole === 'root';

  const [expanded, setExpanded] = useState(() =>
    Object.fromEntries(NAV.filter((i) => i.children).map((i) => [i.label, i.match.test(pathname)])),
  );

  // Membuka grup begitu pengguna berpindah ke salah satu anaknya - misalnya
  // lewat palet perintah, yang melompat tanpa menyentuh sidebar.
  useEffect(() => {
    setExpanded((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const item of NAV) {
        if (item.children && item.match.test(pathname) && !prev[item.label]) {
          next[item.label] = true;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [pathname]);

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 z-40 bg-brand-950/40 lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex w-64 flex-col',
          'bg-[var(--sidebar-bg)] text-[var(--sidebar-text)]',
          'transition-transform duration-300 ease-[var(--ease-out-quart)]',
          open ? 'translate-x-0' : '-translate-x-full',
          'lg:translate-x-0',
        )}
        aria-label="Navigasi utama"
      >
        {/* Satu blok kepala, bukan dua.

            Dulu baris logo dan blok "Ruang Kerja" masing-masing punya garis
            pemisah sendiri, jadi ada dua garis horizontal yang harus dilewati
            sebelum sampai ke menu pertama. Keduanya memakan 112 piksel di
            ruang paling atas, yang justru paling mahal. */}
        <div className="flex h-16 shrink-0 items-center gap-2 border-b border-[var(--sidebar-border)] px-5">
          <Logo variant="light" />

          {organization && (
            <span
              title={organization.name}
              className="ml-auto max-w-[52%] truncate rounded-full border border-white/10 bg-white/[0.07] px-2 py-[3px] text-[11px] font-semibold text-white/65"
            >
              {organization.name}
            </span>
          )}

          <button
            type="button"
            onClick={onClose}
            aria-label="Tutup menu"
            className="-mr-1.5 shrink-0 rounded-md p-1.5 hover:bg-white/10 lg:hidden"
          >
            <X className="size-5" />
          </button>
        </div>

        <nav className="min-h-0 flex-1 space-y-0.5 overflow-y-auto px-3 py-4">
          {NAV.map((item) =>
            item.children ? (
              <div key={item.label}>
                <button
                  type="button"
                  onClick={() => setExpanded((p) => ({ ...p, [item.label]: !p[item.label] }))}
                  aria-expanded={expanded[item.label]}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium',
                    'transition-colors duration-150 hover:bg-white/[0.07] hover:text-white',
                    item.match.test(pathname) && 'font-semibold text-white',
                  )}
                >
                  <item.icon className="size-[18px] shrink-0" aria-hidden="true" />
                  <span className="flex-1 text-left">{item.label}</span>
                  <ChevronDown
                    className={cn(
                      'size-4 shrink-0 transition-transform duration-200',
                      expanded[item.label] && 'rotate-180',
                    )}
                    aria-hidden="true"
                  />
                </button>

                {expanded[item.label] && (
                  <div className="mt-0.5 space-y-px pl-6">
                    {item.children.map((child) => (
                      <NavLink
                        key={child.to}
                        to={child.to}
                        end={child.end}
                        onClick={onClose}
                        className={itemAnak}
                      >
                        <span className="truncate">{child.label}</span>
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <NavLink key={item.to} to={item.to} onClick={onClose} className={itemUtama}>
                <item.icon className="size-[18px] shrink-0" aria-hidden="true" />
                <span className="truncate">{item.label}</span>
              </NavLink>
            ),
          )}

          {lihatManajemen && (
            <>
              <Divider label="Manajemen Web" jumlah={MANAJEMEN.length} />
              {MANAJEMEN.map((item) => (
                <NavLink key={item.to} to={item.to} onClick={onClose} className={itemSekunder}>
                  <span className="truncate">{item.label}</span>
                </NavLink>
              ))}
            </>
          )}

          {lihatDev && (
            <>
              <Divider label="Dev" jumlah={DEV.length} />
              {DEV.map((item) => (
                <NavLink key={item.to} to={item.to} onClick={onClose} className={itemSekunder}>
                  <span className="truncate">{item.label}</span>
                </NavLink>
              ))}
            </>
          )}

          <Divider label="Bantuan" />
          {/* Dibuka di tab baru: dokumentasi biasanya dibaca SAMBIL
              mengerjakan sesuatu, dan menggantikan halaman yang sedang
              dipakai justru memaksa pengguna bolak-balik. */}
          <a
            href="/dokumentasi"
            target="_blank"
            rel="noreferrer"
            onClick={onClose}
            className={itemSekunder({ isActive: false })}
          >
            <span className="truncate">Dokumentasi</span>
          </a>
          <NavLink to="/lapor-bug" onClick={onClose} className={itemSekunder}>
            <span className="truncate">Lapor Bug</span>
          </NavLink>

          {/* Turun ke paling bawah.

              Dulu "Segera Hadir" duduk di tengah dan memotong alur baca
              dengan menu yang justru belum bisa dipakai. Yang belum ada tidak
              pantas menempati posisi lebih tinggi daripada yang sudah ada. */}
          {SOON.map((item) => (
            <div
              key={item.label}
              // Bukan tombol: tidak ada yang terjadi saat diklik, dan elemen
              // yang bisa difokus keyboard tapi tidak melakukan apa-apa justru
              // menyesatkan.
              className="flex cursor-not-allowed items-center gap-2 rounded-md py-[5px] pr-3 pl-6 text-[13px] text-white/25"
              title={`${item.label} - belum tersedia`}
            >
              <span className="flex-1 truncate">{item.label}</span>
              <span className="rounded-full border border-white/15 px-1.5 py-0.5 text-[9px] font-bold tracking-wide">
                SOON
              </span>
            </div>
          ))}
        </nav>

        <div className="shrink-0 border-t border-[var(--sidebar-border)] p-3">
          <div className="flex items-center gap-3 rounded-lg px-2 py-2">
            <div className="grid size-8 shrink-0 place-items-center rounded-full bg-accent-600 text-xs font-bold text-white">
              {initials(user?.name)}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-white">{user?.name}</p>
              <p className="truncate text-xs text-white/50">{user?.email}</p>
            </div>
            <NavLink
              to="/settings"
              onClick={onClose}
              aria-label="Pengaturan"
              title="Pengaturan"
              className="shrink-0 rounded-md p-1.5 text-white/50 transition-colors hover:bg-white/10 hover:text-white"
            >
              <Settings className="size-4" />
            </NavLink>
            <button
              type="button"
              onClick={logout}
              aria-label="Keluar dari akun"
              title="Keluar"
              className="shrink-0 rounded-md p-1.5 text-white/50 transition-colors hover:bg-white/10 hover:text-white"
            >
              <LogOut className="size-4" />
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

/**
 * Pemisah wilayah menu.
 *
 * Garisnya bukan hiasan: tanpa itu label hanya melayang di antara baris dan
 * grupnya tidak terbaca sebagai grup, cuma sebagai baris lain yang kebetulan
 * dicetak berbeda. Jumlahnya ikut ditampilkan supaya "Manajemen Web 11"
 * terbaca sebagai satu wilayah, bukan sebelas baris lepas.
 */
const Divider = ({ label, jumlah }) => (
  <div className="flex items-center gap-2.5 px-3 pt-5 pb-1.5">
    <p className="shrink-0 text-[10px] font-bold tracking-wider text-white/35 uppercase">{label}</p>
    <span className="h-px flex-1 bg-white/10" aria-hidden="true" />
    {jumlah > 0 && (
      <span className="shrink-0 text-[10px] font-semibold tabular-nums text-white/25">
        {jumlah}
      </span>
    )}
  </div>
);
